import axios from 'axios';
import useModal from '@use/useModal/useModal';
import useLoader from '@use/useLoader/useLoader';
import { usePersonalStore } from '@/storePinia/stores/personal';
import { useResultsStore } from '@/storePinia/stores/results';
import BlackListModal from '@components/BlackList/BlackListModal.vue';

export type IBlackListMessages = {
  title?: string;
};

export interface IBlackListItem {
  ID?: string;
  UF_USER_PHONE?: string;
  UF_USER_ID?: string;
  UF_MANAGER_ID?: string;
  UF_MANAGER_NAME?: string;
  UF_MANAGER_EMAIL?: string;
  UF_REASON?: string;
  UF_ACTIVE?: string;
  UF_IS_SENT_BLOCKING_REPORT?: string;
  UF_DATE_UPDATE?: {
    [name: string]: unknown;
  };
  messages?: IBlackListMessages;
}

/** результат перевірки: null — не в чорному списку */
type IBlackListResult = IBlackListItem | null;

// кеш перевірок гостей по телефону, щоб не робити повторних запитів
const guestCache = new Map<string, IBlackListResult>();

const normalizePhone = (phone: string) => phone.replace(/\D/g, '');

interface IResponse {
  isUserInBlackList: boolean;
  result: IBlackListItem;
}

/**
 * TODO: формат відповіді checkUserInBlackList ще не відомий —
 * подивитися в консолі (console.log нижче) і поправити розбір
 */
const parseCheckResponse = (data: IResponse): IBlackListResult => {
  const isBlocked = data.isUserInBlackList;

  return isBlocked ? (data?.result ?? {}) : null;
};

export default function useBlackList() {
  const personalStore = usePersonalStore();
  const resultsStore = useResultsStore();
  const modal = useModal();
  const loader = useLoader();

  /** чорний список авторизованого юзера (приходить з бекенду в authInfo) */
  const getAuthBlackList = (): IBlackListResult => {
    const blackList = personalStore.authInfo?.blackList;
    return blackList?.ID ? blackList : null;
  };

  /** перевірка гостя по телефону */
  const checkGuest = async (phone: string): Promise<IBlackListResult> => {
    const route = resultsStore.routes?.blackList?.checkUserInBlackList;
    const normalized = normalizePhone(phone);
    if (!route || !normalized) return null;

    if (guestCache.has(normalized)) return guestCache.get(normalized) ?? null;

    try {
      loader.start();
      const { data }: { data: IResponse } = await axios.post(route.path, {
        action: route.action,
        LANGUAGE_ID: route.LANGUAGE_ID,
        phone,
      });

      const result = parseCheckResponse(data);

      guestCache.set(normalized, result);
      return result;
    } catch (err) {
      // якщо перевірка впала — не блокуємо юзера, бекенд все одно перевіряє
      console.error(err);
      return null;
    } finally {
      loader.stop();
    }
  };

  const check = async (phone?: string | null): Promise<IBlackListResult> => {
    if (personalStore.isAuthorized) return getAuthBlackList();
    if (phone) return checkGuest(phone);
    return null;
  };

  const showModal = (blackList: IBlackListItem) => {
    // zIndex вище за ModalAside (10003), бо частина сценаріїв відкрита в aside
    modal.show(BlackListModal, { blackList }, { width: '500px', zIndex: 10004 });
  };

  /**
   * Викликати перед відправкою замовлення/форми.
   * Повертає false і показує модалку, якщо юзер у чорному списку.
   * Для гостя без телефону повертає true — перевірка буде на чекауті.
   */
  const ensureNotBlocked = async (phone?: string | null): Promise<boolean> => {
    const result = await check(phone);
    if (!result) return true;

    showModal(result);
    return false;
  };

  return { check, ensureNotBlocked, showModal };
}
